/**
 * driveSpec — Google Drive + OneDrive + SharePoint + Notion.
 *
 * v1 source-of-truth:
 *   ando-frontend/app/pages/DrivePage.tsx (29 — uses shared CloudStoragePage)
 *   ando-frontend/app/pages/OneDrivePage.tsx (29)
 *   ando-frontend/app/pages/SharePointPage.tsx (34)
 *   ando-frontend/app/pages/NotionPage.tsx (391)
 *   ando-frontend/app/pages/shared/CloudStoragePage.tsx
 *
 * Notion is the outlier: workspace picker + tracked-pages CRUD. The v2
 * wizard stops at the OAuth handshake; tracked-pages curation happens
 * post-connect inside the Documents tab.
 */

import type { BaseConnection, CapabilityWizardSpec } from "@ando/ui";
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { FolderTree, Lock, Shield, Sparkles } from "lucide-react";

import {
  GoogleDriveIcon,
  NotionIcon,
  OneDriveIcon,
  SharePointIcon,
} from "../components/vendorIcons";
import { createCapabilityApi } from "../api/capabilityApi";

export interface DriveConnection extends BaseConnection {
  readonly provider?: string; // google_drive | onedrive | sharepoint | notion
  readonly account_email?: string;
  readonly tracked_folder_count?: number;
}

const iconCls = "h-4 w-4";

// Source of truth for the 4 cloud-provider IDs lives in
// ``@ando/core/api/endpoints/driveManagement.ts`` — the BE-contract
// module that also owns the ``DriveProvider`` type and the
// ``asDriveProvider()`` narrowing helper. The pre-fix duplicate set in
// THIS file was a DRY drift hazard (reviewer flagged June 4 2026): two
// parallel allow-lists that compiled but could silently drift when a
// fifth vendor was added. Consumers should ``import { asDriveProvider }
// from "@ando/core"`` and branch on its ``DriveProvider | null`` return
// — that gives the same gate without a second SoT.

export const driveSpec: CapabilityWizardSpec<DriveConnection> = {
  capability: "drive",
  title: msg`Documenti cloud`,
  subtitle: msg`Drive, OneDrive, SharePoint, Notion — file indicizzati e citati`,
  icon: <FolderTree className="h-5 w-5" aria-hidden />,
  gdprDeleteWindowMinutes: 30,

  // June 4 2026 — V1 audit (G3). "Account" is misleading for cloud drives
  // (the user is attaching a workspace/drive, not creating an account).
  // The DRY ``resolveConnectNoun`` produces:
  //   "+ Aggiungi drive" / "Drive collegati" / "3 drive collegati"
  // ``drive`` is invariant in Italian plural, masculine. See
  // ``filesystem``→``cartella`` and ``spreadsheet``→``file`` for the same
  // pattern.
  connectNoun: "drive",
  connectNounPlural: "drive",
  connectNounGender: "m",

  // Real brand icons (V1 audit G2) — V1's ``ProviderIcon`` switch maps each
  // provider to its trademark SVG. The previous generic ``<Cloud>`` for the
  // first 3 vendors made the +Aggiungi dropdown unreadable (4 identical icons
  // when 4 distinct services are listed).
  vendors: [
    {
      id: "google_drive",
      label: "Google Drive",
      icon: <GoogleDriveIcon className={iconCls} />,
    },
    {
      id: "onedrive",
      label: "OneDrive",
      icon: <OneDriveIcon className={iconCls} />,
    },
    {
      id: "sharepoint",
      label: "SharePoint",
      icon: <SharePointIcon className={iconCls} />,
    },
    {
      id: "notion",
      label: "Notion",
      icon: <NotionIcon className={iconCls} />,
    },
  ],

  // ``vendor_picker`` retired May 24 2026 — picker is now AddAccountMenu.
  steps: [
    {
      id: "authorize",
      kind: "oauth",
      title: msg`Autorizza`,
      description: msg`Sceglierai dopo quali cartelle / pagine indicizzare.`,
      oauth: { buttonLabel: msg`Autorizza accesso` },
    },
  ],

  benefits: [
    {
      icon: <FolderTree className={iconCls} aria-hidden />,
      title: msg`Selective sync`,
      body: msg`Indicizza solo le cartelle che scegli — il resto resta privato.`,
    },
    {
      icon: <Sparkles className={iconCls} aria-hidden />,
      title: msg`Q&A sui file`,
      body: msg`"Cosa c'è nella due-diligence Acme?" → risposta con citazioni.`,
    },
    {
      icon: <Lock className={iconCls} aria-hidden />,
      title: msg`Permessi rispettati`,
      body: msg`Ando vede solo ciò che l'utente vede sul provider.`,
    },
    {
      icon: <Shield className={iconCls} aria-hidden />,
      title: msg`Cancellazione GDPR`,
      body: msg`Sganci → indice eliminato entro la finestra di compliance.`,
    },
  ],

  api: createCapabilityApi<DriveConnection>({ capability: "drive" }),
  connectionKeyField: "id" as keyof DriveConnection,
  // Italian UI fallback — last-resort label when the BE returns neither
  // ``account_email`` nor an ``id`` (defensive; should never hit in prod).
  // Matches ``connectNoun`` for consistency: row reads "Drive · sync 3h fa".
  getDisplayName: (c) => c.account_email ?? c.id ?? i18nSingleton._(msg`Drive`),
  getSecondaryId: (c) => (c.provider ? c.provider : null),
};
